<script setup>
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

/**
 * Label/payload list editor shared by the button-bearing nodes (question,
 * quickreply) and the option-bearing ones (radio, multiselect, checkbox,
 * dropdown) - both shapes are `{ label, payload }[]`.
 */
const props = defineProps({
  items: { type: Array, default: () => [] },
  title: { type: String, required: true },
  addLabel: { type: String, required: true },
  payloadPrefix: { type: String, default: 'opt' },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const updateItem = (index, patch) => {
  emit(
    'update',
    props.items.map((item, i) => (i === index ? { ...item, ...patch } : item))
  );
};

const removeItem = index => {
  emit(
    'update',
    props.items.filter((_, i) => i !== index)
  );
};

const moveItem = (index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= props.items.length) return;
  const next = [...props.items];
  [next[index], next[target]] = [next[target], next[index]];
  emit('update', next);
};

const addItem = () => {
  const count = props.items.length + 1;
  emit('update', [
    ...props.items,
    {
      label: `${t('AGENT_BOTS.VISUAL_BUILDER.OPTION')} ${count}`,
      payload: `${props.payloadPrefix}${count}`,
    },
  ]);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <p class="text-xs font-medium text-n-slate-12">{{ title }}</p>
    <div
      v-for="(item, idx) in items"
      :key="`item-${idx}`"
      class="flex flex-col gap-1 p-2 border rounded-lg border-n-weak"
    >
      <div class="flex items-center justify-between">
        <span class="text-[10px] font-semibold text-n-slate-10">
          {{ idx + 1 }}
        </span>
        <div class="flex items-center gap-0.5">
          <Button
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-lucide-arrow-up"
            is-icon-only
            :disabled="idx === 0"
            :title="t('AGENT_BOTS.VISUAL_BUILDER.MOVE_UP')"
            @click="moveItem(idx, -1)"
          />
          <Button
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-lucide-arrow-down"
            is-icon-only
            :disabled="idx === items.length - 1"
            :title="t('AGENT_BOTS.VISUAL_BUILDER.MOVE_DOWN')"
            @click="moveItem(idx, 1)"
          />
          <Button
            size="xs"
            variant="ghost"
            color="ruby"
            icon="i-lucide-trash-2"
            is-icon-only
            :title="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
            @click="removeItem(idx)"
          />
        </div>
      </div>
      <Input
        :model-value="item.label"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.LABEL')"
        @update:model-value="updateItem(idx, { label: $event })"
      />
      <Input
        :model-value="item.payload"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.PAYLOAD')"
        @update:model-value="updateItem(idx, { payload: $event })"
      />
    </div>
    <Button
      size="sm"
      color="blue"
      variant="faded"
      icon="i-lucide-plus"
      :label="addLabel"
      @click="addItem"
    />
  </div>
</template>
